import {ModeSettingEnum, VictoryPointTypes} from '@colonist/game-base-types'
import {PlayerColor} from '@colonist/game-states'
import {TranslationObject} from '@colonist/i18n'
import IconChit from '@static/images/icon_chit.svg'
import IconMetropolisScience from '@static/images/metropolis_science.svg'
import IconMetropolisTrade from '@static/images/metropolis_trade.svg'
import IconMetropolisPolitics from '@static/images/metropolis_politics.svg'
// eslint-disable-next-line no-restricted-imports
import {UIImagePathController} from '../../../../../../../code/client/shared/scripts/UIEnums.ts'
import CardVP from '@static/images/card_vp.svg'
import IconLargestArmyHighlight from '@static/images/icon_largest_army_highlight.svg'
import IconLongestRoadHighlight from '@static/images/icon_longest_road_highlight.svg'
import IconDefender from '@static/images/icon_defender.svg'
import CardProgressSciencePrinter from '@static/images/card_progress_science_printer.svg'
import CardProgressPoliticsConstitution from '@static/images/card_progress_politics_constitution.svg'
import {ImageURL} from '@colonist/types'

export interface VPInformation {
    type: VictoryPointTypes
    vpAmount: number
    title: TranslationObject
    description?: TranslationObject
    secondaryIcon?: ImageURL
}

const settlementVPInformation: VPInformation = {
    type: VictoryPointTypes.Settlement,
    vpAmount: 1,
    title: {key: 'strings:game.VictoryPointTypes.Settlement.title'},
    description: {key: 'strings:game.VictoryPointTypes.Settlement.description'},
}

const cityVPInformation: VPInformation = {
    type: VictoryPointTypes.City,
    vpAmount: 1,
    title: {key: 'strings:game.VictoryPointTypes.City.title'},
    description: {key: 'strings:game.VictoryPointTypes.City.description'},
}

const developmentCardVPInformation: VPInformation = {
    type: VictoryPointTypes.DevelopmentCardVictoryPoint,
    vpAmount: 1,
    title: {key: 'strings:game.VictoryPointTypes.DevelopmentCardVictoryPoint.title'},
    description: {key: 'strings:game.VictoryPointTypes.DevelopmentCardVictoryPoint.description'},
}

const largestArmyVPInformation: VPInformation = {
    type: VictoryPointTypes.LargestArmy,
    vpAmount: 2,
    title: {key: 'strings:game.VictoryPointTypes.LargestArmy.title'},
    description: {key: 'strings:game.VictoryPointTypes.LargestArmy.description'},
}

const longestRoadVPInformation: VPInformation = {
    type: VictoryPointTypes.LongestRoad,
    vpAmount: 2,
    title: {key: 'strings:game.VictoryPointTypes.LongestRoad.title'},
    description: {key: 'strings:game.VictoryPointTypes.LongestRoad.description'},
}

const chitsVPInformation: VPInformation = {
    type: VictoryPointTypes.Chits,
    vpAmount: 2,
    title: {key: 'strings:game.VictoryPointTypes.Chits.title'},
    description: {key: 'strings:game.VictoryPointTypes.Chits.description'},
    secondaryIcon: IconChit,
}

const scienceMetropolisVPInformation: VPInformation = {
    type: VictoryPointTypes.Metropolis,
    vpAmount: 2,
    title: {key: 'strings:game.VictoryPointTypes.Metropolis.title', options: {context: 'Science'}},
    description: {key: 'strings:game.VictoryPointTypes.Metropolis.description', options: {context: 'Science'}},
    secondaryIcon: IconMetropolisScience,
}

const tradeMetropolisVPInformation: VPInformation = {
    type: VictoryPointTypes.Metropolis,
    vpAmount: 2,
    title: {key: 'strings:game.VictoryPointTypes.Metropolis.title', options: {context: 'Trade'}},
    description: {key: 'strings:game.VictoryPointTypes.Metropolis.description', options: {context: 'Trade'}},
    secondaryIcon: IconMetropolisTrade,
}

const politicsMetropolisVPInformation: VPInformation = {
    type: VictoryPointTypes.Metropolis,
    vpAmount: 2,
    title: {key: 'strings:game.VictoryPointTypes.Metropolis.title', options: {context: 'Politics'}},
    description: {key: 'strings:game.VictoryPointTypes.Metropolis.description', options: {context: 'Politics'}},
    secondaryIcon: IconMetropolisPolitics,
}

const defenderOfColonistVPInformation: VPInformation = {
    type: VictoryPointTypes.DefenderOfColonist,
    vpAmount: 1,
    title: {key: 'strings:game.VictoryPointTypes.DefenderOfColonist.title'},
    description: {key: 'strings:game.VictoryPointTypes.DefenderOfColonist.description'},
}

const printerProgressCardVPInformation: VPInformation = {
    type: VictoryPointTypes.ProgressCardVictoryPoint,
    vpAmount: 1,
    title: {key: 'strings:game.VictoryPointTypes.ProgressCardVictoryPoint.title', options: {context: 'Printer'}},
    description: {key: 'strings:game.VictoryPointTypes.ProgressCardVictoryPoint.description', options: {context: 'Printer'}},
}

const constitutionProgressCardVPInformation: VPInformation = {
    type: VictoryPointTypes.ProgressCardVictoryPoint,
    vpAmount: 1,
    title: {key: 'strings:game.VictoryPointTypes.ProgressCardVictoryPoint.title', options: {context: 'Constitution'}},
    description: {key: 'strings:game.VictoryPointTypes.ProgressCardVictoryPoint.description', options: {context: 'Constitution'}},
}

const merchantVPInformation: VPInformation = {
    type: VictoryPointTypes.Merchant,
    vpAmount: 1,
    title: {key: 'strings:game.VictoryPointTypes.Merchant.title'},
    description: {key: 'strings:game.VictoryPointTypes.Merchant.description'},
}

const baseGameVPInformation: VPInformation[] = [
    settlementVPInformation,
    cityVPInformation,
    longestRoadVPInformation,
]

const seafarersVPInformation: VPInformation[] = [
    chitsVPInformation,
]

const citiesAndKnightsVPInformation: VPInformation[] = [
    merchantVPInformation,
    defenderOfColonistVPInformation,
    printerProgressCardVPInformation,
    constitutionProgressCardVPInformation,
    tradeMetropolisVPInformation,
    scienceMetropolisVPInformation,
    politicsMetropolisVPInformation,
]

export function getVPInformation(gameMode: ModeSettingEnum): VPInformation[] {
    const baseGameVPs = [...baseGameVPInformation, largestArmyVPInformation, developmentCardVPInformation]
    const sortedBaseGameVPs = baseGameVPs.sort((a: VPInformation, b: VPInformation) => a.vpAmount - b.vpAmount)

    const seafarersVPs = [...baseGameVPInformation, largestArmyVPInformation, ...seafarersVPInformation, developmentCardVPInformation]
    const sortedSeafarersVPs = seafarersVPs.sort((a: VPInformation, b: VPInformation) => a.vpAmount - b.vpAmount)

    const citiesAndKnightsVPs = [...baseGameVPInformation, ...citiesAndKnightsVPInformation]
    const sortedCitiesAndKnightsVPs = citiesAndKnightsVPs.sort((a: VPInformation, b: VPInformation) => a.vpAmount - b.vpAmount)

    const citiesAndKnightsSeafarersVPs = [...baseGameVPInformation, ...seafarersVPInformation, ...citiesAndKnightsVPInformation]
    const sortedCitiesAndKnightsSeafarersVPs = citiesAndKnightsSeafarersVPs.sort((a: VPInformation, b: VPInformation) => a.vpAmount - b.vpAmount)

    switch(gameMode) {
        case ModeSettingEnum.Seafarers4P:
        case ModeSettingEnum.Seafarers56P:
            return sortedSeafarersVPs
        case ModeSettingEnum.CitiesAndKnights4P:
        case ModeSettingEnum.CitiesAndKnights56P:
            return sortedCitiesAndKnightsVPs
        case ModeSettingEnum.CitiesAndKnightsSeafarers4P:
        case ModeSettingEnum.CitiesAndKnightsSeafarers56P:
            return sortedCitiesAndKnightsSeafarersVPs
        default:
            return sortedBaseGameVPs
    }
}

export function getVPTypeIconPath(vpInfo: VPInformation, color: PlayerColor) {
    const isPlayerSpectator = color === 0
    color = isPlayerSpectator ? PlayerColor.Blue : color
    switch(vpInfo.type) {
        case VictoryPointTypes.Settlement:
        case VictoryPointTypes.Chits:
            return UIImagePathController.settlementsByColor[color]
        case VictoryPointTypes.City:
        case VictoryPointTypes.Metropolis:
            return UIImagePathController.citiesByColor[color]
        case VictoryPointTypes.DevelopmentCardVictoryPoint:
            return CardVP
        case VictoryPointTypes.LargestArmy:
            return IconLargestArmyHighlight
        case VictoryPointTypes.LongestRoad:
            return IconLongestRoadHighlight
        case VictoryPointTypes.DefenderOfColonist:
            return IconDefender
        case VictoryPointTypes.Merchant:
            return UIImagePathController.merchantByColor[color]
        case VictoryPointTypes.ProgressCardVictoryPoint:
            const isPrinter = vpInfo.title == printerProgressCardVPInformation.title
            return isPrinter ? CardProgressSciencePrinter : CardProgressPoliticsConstitution
        default:
            return UIImagePathController.settlementsByColor[color]
    }
}
